import { useRef, useState } from "react";
import { useInView } from "framer-motion";
import { Mail, Phone, MessageSquare, Search, Database, Calendar, UserRound, RotateCcw } from "lucide-react";
import "./ServiceIllustration.css";

const Circuit = ({ d, delay = 0 }) => (
  <g>
    <path d={d} className="schematic-track" />
    <path d={d} pathLength="1" className="schematic-packet" style={{ "--packet-delay": `${delay}s` }} />
  </g>
);

const BuyerRadar = () => (
  <>
    <rect x="20" y="10" width="160" height="160" className="schematic-panel" />
    <circle cx="100" cy="90" r="65" className="schematic-grid" />
    <circle cx="100" cy="90" r="42" className="schematic-grid" />
    <path d="M 20 90 H 180 M 100 10 V 170" className="schematic-grid" />
    <g className="buyer-radar-sweep">
      <path d="M 100 90 L 100 25 A 65 65 0 0 1 156 57 Z" fill="#FF4400" fillOpacity=".2" />
      <path d="M 100 90 V 25" stroke="#FF4400" strokeWidth="2" />
    </g>
    {[[68, 61], [124, 117], [139, 61]].map(([x, y], i) => (
      <g key={x} className="buyer-radar-lock" style={{ "--lock-delay": `${i * .45}s`, transformOrigin: `${x}px ${y}px` }}>
        <rect x={x - 6} y={y - 6} width="12" height="12" fill={i === 2 ? "#FF4400" : "#1B36F9"} />
        <rect x={x - 11} y={y - 11} width="22" height="22" fill="none" stroke={i === 2 ? "#FF4400" : "#1B36F9"} />
      </g>
    ))}
    <Circuit d="M 180 90 H 210 V 43 H 252" />
    <Circuit d="M 180 90 H 252" delay={.25} />
    <Circuit d="M 180 90 H 210 V 137 H 252" delay={.5} />
    {[26, 73, 120].map((y, i) => (
      <g key={y}>
        <rect x="252" y={y} width="130" height="34" fill={i === 2 ? "#1B36F9" : "#E8E4D9"} stroke="#1B36F9" />
        <circle cx="271" cy={y + 17} r="7" fill={i === 2 ? "#FF4400" : "#1B36F9"} />
        <path d={`M 288 ${y + 12} H 364 M 288 ${y + 22} H 339`} stroke={i === 2 ? "#FFFFFF" : "#1B36F9"} strokeWidth="3" />
      </g>
    ))}
  </>
);

const AttentionNetwork = () => (
  <>
    <Circuit d="M 200 90 H 140 V 40 H 65" />
    <Circuit d="M 200 90 H 285 V 40 H 340" delay={.2} />
    <Circuit d="M 200 90 H 140 V 140 H 65" delay={.4} />
    <Circuit d="M 200 90 H 285 V 140 H 340" delay={.6} />
    <g className="attention-hub">
      <rect x="165" y="55" width="70" height="70" fill="#FF4400" />
      <path d="M 185 72 H 216 V 96 H 201 L 187 108 V 96 H 185 Z" fill="none" stroke="#0F1115" strokeWidth="3" />
    </g>
    {[[45, 20, Mail], [320, 20, Phone], [45, 120, MessageSquare], [320, 120, UserRound]].map(([x, y, Icon], i) => (
      <g key={x + y} className="attention-channel" style={{ "--channel-delay": `${.2 + i * .2}s`, transformOrigin: `${x + 20}px ${y + 20}px` }}>
        <rect x={x - 7} y={y - 7} width="54" height="54" fill="#E8E4D9" stroke="#1B36F9" strokeWidth="2" />
        <Icon x={x + 5} y={y + 5} width="30" height="30" color="#1B36F9" strokeWidth="1.8" />
      </g>
    ))}
  </>
);

const SignedDeal = () => (
  <>
    <path d="M 61 15 H 296 L 332 51 V 164 H 61 Z" fill="#E8E4D9" stroke="#0F1115" />
    <path d="M 296 15 V 51 H 332" fill="#DCD7C9" stroke="#0F1115" />
    <rect x="81" y="36" width="24" height="24" fill="#1B36F9" />
    <path d="M 119 43 H 221 M 119 54 H 174 M 81 80 H 291 M 81 92 H 273" className="contract-copy" />
    <path d="M 81 143 H 239" stroke="#0F1115" strokeOpacity=".25" />
    <path className="contract-signature" pathLength="1" d="M 89 136 C 119 91 124 105 100 134 C 132 108 134 131 149 121 C 168 107 141 148 164 134 C 184 117 186 126 195 129 C 205 133 215 117 229 121" />
    <g className="contract-seal">
      <circle cx="320" cy="133" r="31" fill="#FF4400" stroke="#0F1115" strokeWidth="2" />
      <path d="M 305 133 L 315 143 L 336 122" fill="none" stroke="#0F1115" strokeWidth="4" />
    </g>
  </>
);

const SellingTime = () => (
  <>
    {[Search, Database, Calendar].map((Icon, i) => (
      <g key={i}>
        <rect x="27" y={15 + i * 57} width="42" height="42" className="schematic-panel" />
        <Icon x="38" y={26 + i * 57} width="20" height="20" color="#1B36F9" />
      </g>
    ))}
    <Circuit d="M 69 36 H 118 V 90 H 172" />
    <Circuit d="M 69 93 H 172" delay={.2} />
    <Circuit d="M 69 150 H 118 V 90 H 172" delay={.4} />
    <rect x="172" y="52" width="76" height="76" fill="#1B36F9" />
    <g className="operator-rotor">
      {[0, 45, 90, 135].map(angle => <rect key={angle} x="206" y="68" width="8" height="44" rx="1" fill="#E8E4D9" transform={`rotate(${angle} 210 90)`} />)}
      <circle cx="210" cy="90" r="9" fill="#FF4400" />
    </g>
    <Circuit d="M 248 90 H 322" delay={.6} />
    <circle cx="350" cy="90" r="35" fill="#FF4400" />
    <UserRound x="332" y="71" width="36" height="36" color="#0F1115" strokeWidth="1.8" />
    <path d="M 389 78 Q 400 90 389 102 M 397 70 Q 414 90 397 110" fill="none" stroke="#1B36F9" strokeWidth="3" className="selling-voice" />
  </>
);

const illustrations = [BuyerRadar, AttentionNetwork, SignedDeal, SellingTime];
const names = ["buyer radar", "outreach channels", "signed deal", "selling time"];

export const ServiceIllustration = ({ index }) => {
  const ref = useRef(null);
  const entered = useInView(ref, { once: true, amount: .4 });
  const [replay, setReplay] = useState(0);
  const Illustration = illustrations[index];
  return (
    <div ref={ref} className="service-visual" data-entered={entered}>
      <svg key={replay} viewBox="0 0 440 180" aria-hidden="true"><Illustration /></svg>
      <button type="button" className="service-visual-replay" onClick={() => setReplay(value => value + 1)} aria-label={`Replay ${names[index]} animation`}>
        <RotateCcw size={16} aria-hidden="true" /> Replay
      </button>
    </div>
  );
};
